import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export default function PaymentSuccessPage({ token }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Legacy Payment Link parameters
  const razorpayPaymentId = searchParams.get("razorpay_payment_id");
  const paymentLinkId = searchParams.get("razorpay_payment_link_id");
  const paymentStatus = searchParams.get("razorpay_payment_link_status");

  // New Checkout system parameters
  const orderId = searchParams.get("order_id");
  const paymentId = searchParams.get("payment_id");

  // Determine which system was used
  const isNewCheckout = orderId && paymentId;
  const isLegacyPaymentLink = paymentStatus === "paid" && razorpayPaymentId;

  const handleClose = () => {
    // Check if we came from checkout page
    const referrer = document.referrer;
    const cameFromCheckout = referrer && referrer.includes("/checkout/");
    
    // Always try to close the window first
    // This works if the window was opened by JavaScript (window.open) or in Instagram webview
    window.close();
    
    // If window.close() doesn't work (browsers block it for security),
    // handle based on where we came from
    setTimeout(() => {
      if (cameFromCheckout) {
        // If we came from checkout, we want to close the entire flow
        // Since checkout page was replaced in history (via location.replace),
        // going back would skip checkout and go to the page before it
        // For Instagram webview, we can't reliably close, so just stay on success page
        // The user will need to manually close or return to Instagram
        console.log("Payment successful. Please return to Instagram.");
      } else {
        // If we didn't come from checkout, go back in history
        if (window.history && window.history.length > 1) {
          window.history.back();
        }
      }
    }, 100);
  };

  useEffect(() => {
    // Check if payment is successful (either system)
    if (isNewCheckout || isLegacyPaymentLink) {
      setLoading(false);
      // Payment is successful, order should be created via webhook or verify endpoint
      // Check if we came from checkout - if so, don't auto-close (let user manually close)
      // This prevents the jarring experience of auto-closing and going back to checkout
      const referrer = document.referrer;
      const cameFromCheckout = referrer && referrer.includes("/checkout/");
      
      if (!cameFromCheckout) {
        // Only auto-close if we didn't come from checkout (e.g., direct link)
        const timer = setTimeout(() => {
          handleClose();
        }, 3000); // Auto-close after 3 seconds
        return () => clearTimeout(timer);
      }
    } else if (paymentStatus === "cancelled") {
      setError("Payment was cancelled");
      setLoading(false);
    } else {
      setError("Payment status unknown");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewCheckout, isLegacyPaymentLink, paymentStatus]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: "center" }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={handleClose}>
            Close Window
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        p: 3,
        backgroundColor: "#f5f5f5",
      }}
    >
      <Paper
        sx={{
          p: 4,
          maxWidth: 500,
          textAlign: "center",
          borderRadius: 2,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 80, color: "success.main", mb: 2 }} />
        <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
          Payment Successful! ✅
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Your payment has been processed successfully.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your order will be processed shortly. You can now close this window
          and return to Instagram.
        </Typography>

        {/* Show Order ID (new system) or Payment ID (legacy system) */}
        {orderId && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block", fontFamily: "monospace" }}
          >
            Order ID: {orderId}
          </Typography>
        )}
        {(paymentId || razorpayPaymentId) && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 3, display: "block", fontFamily: "monospace" }}
          >
            Payment ID: {paymentId || razorpayPaymentId}
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button variant="contained" onClick={handleClose}>
            Close Window
          </Button>
        </Box>
        {(() => {
          const referrer = document.referrer;
          const cameFromCheckout = referrer && referrer.includes("/checkout/");
          if (!cameFromCheckout) {
            return (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2, display: "block" }}
              >
                This window will close automatically in a few seconds...
              </Typography>
            );
          }
          return null;
        })()}
      </Paper>
    </Box>
  );
}
